;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:propstrata-app":true});}catch(e){}}();import * as Sentry from "@sentry/nextjs";
import {
	isDeployedEnvironment,
	isLogLevelEnabled,
	type LogSettings,
	readLogSettings,
} from "@/lib/observability/environment";
import { scrubSensitiveValue } from "@/lib/observability/scrub";

export type LogAttributeValue = string | number | boolean;
export type LogAttributes = Record<string, LogAttributeValue>;
export type AppLogLevel = "debug" | "info" | "warn" | "error";

const MAX_ATTRIBUTE_LENGTH = 512;

/** Logger-only options; they shape the Issue event, not the log. */
const LOGGER_OPTION_KEYS = new Set(["fingerprint", "reportToSentry", "tags"]);

/** Shared attribute names (see spec "Attribute rules"). */
const CONTEXT_KEY_TO_ATTRIBUTE: Record<string, string> = {
	feature: "feature",
	operation: "operation",
	organizationId: "tenant.organization_id",
	outcome: "outcome",
	requestId: "request.id",
	userId: "user.id",
	workspaceId: "tenant.workspace_id",
};

let cachedSettings: LogSettings | undefined;

/** Env doesn't change at runtime. Tests call this after `vi.stubEnv`. */
export function resetLogConfigCache(): void {
	cachedSettings = undefined;
}

function getLogSettings(): LogSettings {
	cachedSettings ??= readLogSettings();
	return cachedSettings;
}

function safeStringify(value: unknown): string {
	try {
		return JSON.stringify(value) ?? String(value);
	} catch {
		return "[Unserializable]";
	}
}

function toAttributeValue(value: unknown): LogAttributeValue {
	if (typeof value === "number" || typeof value === "boolean") {
		return value;
	}
	const text = typeof value === "string" ? value : safeStringify(value);
	return text.length > MAX_ATTRIBUTE_LENGTH
		? `${text.slice(0, MAX_ATTRIBUTE_LENGTH)}…`
		: text;
}

export function toLogAttributes(
	context: Record<string, unknown> | undefined,
): LogAttributes {
	return toScrubbedLogAttributes(
		scrubSensitiveValue(context) as Record<string, unknown> | undefined,
	);
}

/** For a context the caller already scrubbed, to reuse it elsewhere. */
export function toScrubbedLogAttributes(
	scrubbedContext: Record<string, unknown> | undefined,
): LogAttributes {
	if (!scrubbedContext) {
		return {};
	}

	const attributes: LogAttributes = {};
	for (const [key, value] of Object.entries(scrubbedContext)) {
		if (LOGGER_OPTION_KEYS.has(key) || value === undefined || value === null) {
			continue;
		}
		const attributeKey = CONTEXT_KEY_TO_ATTRIBUTE[key] ?? `context.${key}`;
		attributes[attributeKey] = toAttributeValue(value);
	}
	return attributes;
}

function writeConsole({
	attributes,
	environment,
	level,
	message,
}: {
	attributes: LogAttributes;
	environment: string;
	level: AppLogLevel;
	message: string;
}) {
	if (process.env.NODE_ENV === "test") {
		return;
	}
	if (!isDeployedEnvironment(environment)) {
		console[level](`[${level}] ${message}`, attributes);
		return;
	}
	// Cloud Logging keeps a copy of warnings and errors even if Sentry
	// ingestion fails. Browsers have no Cloud Logging: keep devtools quiet.
	const isServer = typeof window === "undefined";
	if (isServer && (level === "error" || level === "warn")) {
		console[level](
			JSON.stringify({
				attributes,
				message,
				severity: level === "error" ? "ERROR" : "WARNING",
			}),
		);
	}
}

export function emitLog({
	attributes,
	level,
	message,
}: {
	attributes: LogAttributes;
	level: AppLogLevel;
	message: string;
}): void {
	const settings = getLogSettings();
	writeConsole({
		attributes,
		environment: settings.environment,
		level,
		message,
	});

	if (!isLogLevelEnabled({ level, threshold: settings.logLevel })) {
		return;
	}
	try {
		Sentry.logger?.[level]?.(message, attributes);
	} catch {
		// Telemetry must never break the caller.
	}
}
